"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isNativePlatform } from "@/lib/runtime";
import { resolveAbsoluteApiUrl } from "@/lib/api";
import { useMediaAuth } from "@/hooks/useMediaAuth";
import { reportProviderClientIssue } from "@/lib/providerClientIssue";
import { isMp3OnlyMode } from "@/lib/mp3OnlyMode";

const CALLBACK_URL =
  process.env.NEXT_PUBLIC_NATIVE_AUTH_CALLBACK_URL || "imags://auth/callback";

function parseGrantFromCallback(url: string): {
  grant: string;
  provider: "spotify" | "youtube";
} | null {
  try {
    const incoming = new URL(url);
    const expected = new URL(CALLBACK_URL);

    const sameCallbackTarget =
      incoming.protocol === expected.protocol &&
      incoming.hostname === expected.hostname &&
      incoming.pathname === expected.pathname;
    if (!sameCallbackTarget) return null;

    const grant = incoming.searchParams.get("grant");
    if (!grant) return null;

    const providerRaw = incoming.searchParams.get("provider");
    const provider =
      providerRaw === "youtube"
        ? "youtube"
        : providerRaw === "spotify"
          ? "spotify"
          : null;
    if (!provider) return null;

    return { grant, provider };
  } catch {
    return null;
  }
}

async function closeNativeBrowser() {
  try {
    await Browser.close();
  } catch {
    // Browser may already be closed; no-op.
  }
}

function ProviderNativeOAuthBridge() {
  const router = useRouter();
  const { checkSession } = useMediaAuth();
  const inFlightRef = useRef(false);
  const consumedGrantsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isNativePlatform()) return;

    const handleUrl = async (url: string) => {
      const parsed = parseGrantFromCallback(url);
      if (!parsed) return;
      if (consumedGrantsRef.current.has(parsed.grant)) return;
      if (inFlightRef.current) return;

      consumedGrantsRef.current.add(parsed.grant);
      inFlightRef.current = true;

      try {
        const completePath =
          parsed.provider === "spotify"
            ? "/api/spotify/native/complete"
            : "/api/youtube/native/complete";
        const completeUrl = new URL(resolveAbsoluteApiUrl(completePath));
        completeUrl.searchParams.set("grant", parsed.grant);
        const res = await fetch(completeUrl, {
          method: "GET",
          credentials: "include",
        });
        const payload = await res.json().catch(() => ({}));

        if (!res.ok || payload?.ok !== true) {
          throw new Error(payload?.error || `Native OAuth bridge failed (${res.status})`);
        }

        await closeNativeBrowser();
        await checkSession();
        const returnTo =
          typeof payload?.returnTo === "string" && payload.returnTo.startsWith("/")
            ? payload.returnTo
            : "/presurvey";
        router.replace(returnTo);
      } catch (err) {
        console.error("[NativeOAuthBridge] Failed to complete auth bridge", err);
        reportProviderClientIssue({
          provider: parsed.provider,
          category: "auth",
          level: "error",
          event: "native_oauth_completion_failed",
          title: `${
            parsed.provider === "spotify" ? "Spotify" : "YouTube"
          } native sign-in failed`,
          message:
            err instanceof Error
              ? err.message
              : "Failed to complete native OAuth bridge",
          error: err,
          details: {
            nativePlatform: true,
          },
        });
      } finally {
        inFlightRef.current = false;
      }
    };

    let removed = false;
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    void (async () => {
      listenerHandle = await App.addListener("appUrlOpen", ({ url }) => {
        if (removed) return;
        void handleUrl(url);
      });

      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        void handleUrl(launch.url);
      }
    })();

    return () => {
      removed = true;
      if (listenerHandle) {
        void listenerHandle.remove();
      }
    };
  }, [checkSession, router]);

  return null;
}

export default function NativeOAuthBridge() {
  if (isMp3OnlyMode) {
    return null;
  }

  return <ProviderNativeOAuthBridge />;
}
